<template>
  <div class="billing-invoice-view">
    <!-- Loader strictly during fetch -->
    <v-loader v-if="loading" />

    <!-- Error State -->
    <div v-else-if="errorMessage" class="text-center py-64 border rounded bg-white shadow-sm">
      <v-icon icon="warning-sign" size="xxl" class="text-danger mb-16" />
      <h5 class="text-dark fw-bold">{{ errorMessage }}</h5>
      <v-button variant="primary" size="sm" class="mt-16" @click="retryLoad">
        {{ $t("general.button.retry") }}
      </v-button>
    </div>

    <!-- Success State: Table only if data exists -->
    <div v-else-if="paxList.length > 0">
      <!-- Quote Summary Card -->
      <div class="quote-summary-card d-flex align-items-center justify-content-between gap-16 px-20 py-12 mb-16 bg-white border rounded-sm shadow-sm">
        <div class="d-flex align-items-center gap-20">
          <div class="d-flex align-items-center justify-content-center" style="width:40px;height:40px;border-radius:8px;background:rgba(253,12,111,0.1);">
            <v-icon icon="bookmark" size="sm" class="text-primary" />
          </div>
          <div>
            <div class="text-xs text-uppercase fw-bold text-muted letter-spacing-1">Generated Quote</div>
            <div class="d-flex align-items-baseline gap-8">
              <span class="h4 mb-0 fw-bold text-dark">{{ format(caseQuotePrice) }}€</span>
              <span v-if="caseQuoteLocked" class="badge bg-warning text-dark">
                <TheLock style="width:10px;height:10px;display:inline;vertical-align:baseline;" /> Locked
              </span>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center gap-20" v-if="quoteHistory.totalQuotes > 0">
          <div class="text-end">
            <div class="text-xs text-uppercase fw-bold text-muted letter-spacing-1">Quotes Sent</div>
            <div class="h5 mb-0 fw-bold text-primary">{{ quoteHistory.totalQuotes }}</div>
          </div>
          <div class="text-end" v-if="quoteHistory.lastSentDate">
            <div class="text-xs text-uppercase fw-bold text-muted letter-spacing-1">Last Sent</div>
            <div class="text-sm fw-semibold text-dark">{{ quoteHistory.lastSentDate }}</div>
          </div>
        </div>
      </div>

      <!-- Action Bar -->
      <div class="d-flex justify-content-end gap-8 w-100 mb-16">
        <v-button
          class="btn-icon w-fit rounded-sm text-uppercase x-small fw-bold px-16 shadow-xs quote-action-btn"
          size="sm"
          variant="outline-secondary"
          @click="startNewQuote"
        >
          <v-icon icon="plus" size="xs" space="me-8" />
          New Quote
        </v-button>
        <v-button
          class="btn-icon w-fit rounded-sm text-uppercase x-small fw-bold px-16 shadow-xs quote-action-btn"
          size="sm"
          variant="primary"
          :disabled="!isAnySelected"
          @click="sendQuoteOffcanvas"
        >
          <v-icon icon="sent" size="xs" space="me-8" />
          Send Quote
        </v-button>
      </div>
      <!-- The common table design -->
      <div class="table-wrap border rounded-sm shadow-sm overflow-hidden bg-white">
        <div class="table-responsive">
          <table class="table table-hover mb-0 align-middle quote-table">
            <thead>
              <tr>
                <th width="40" class="text-center ps-12">
                  <input type="checkbox" class="chk" @change="toggleAll" :checked="isAllSelected" />
                </th>
                <th class="left">Pax</th>
                <th class="text-end">Net Price</th>
                <th class="text-end" width="100">Profit</th>
                <th class="text-end" width="80">Margin %</th>
                <th class="text-end">Gross Price</th>
                <th v-if="!caseIsDraft" class="text-end">Paid Amount</th>
                <th class="text-end" width="130">Waw Price</th>
                <template v-if="!caseIsDraft">
                  <th v-for="key in activeExtraKeys" :key="key" class="text-end">{{ getLabel(key) }}</th>
                  <th width="150">Invoice Type</th>
                </template>
              </tr>
            </thead>

            <tbody>
              <template v-for="(pax, index) in groupedPaxList" :key="pax.id">
              <tr
                :class="[{ 'row-selected': pax.selected }, getBatchRowClass(pax)]"
              >
                <td v-if="getRowSpan(pax, index) !== 0" class="checkbox-cell ps-12" :rowspan="getRowSpan(pax, index)">
                  <input
                    type="checkbox"
                    class="chk cursor-pointer"
                    :checked="pax.selected"
                    :disabled="isCheckboxDisabled(pax)"
                    @change="toggleBatch(pax, $event.target?.checked)"
                  />
                </td>

                <td class="left">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center gap-8">
                      <button type="button" class="btn-expand-inline p-0" @click="toggleExpand(pax.id)">
                        <v-icon :icon="isExpanded(pax.id) ? 'chevron-up' : 'chevron-down'" size="xs" />
                      </button>
                      <span class="fw-bold text-dark">{{ pax.name }}</span>
                    </div>
                    <v-button class="btn-edit-inline p-0" variant="icon" size="xs" @click="openEditPaxModal(pax)">
                      <v-icon icon="pencil" size="xs" />
                    </v-button>
                  </div>
                </td>
                <td class="text-end text-muted">
                  {{ format(pax.net) }}€
                </td>
                <td class="text-end">
                  <input
                    type="number"
                    class="cell-input"
                    v-model.number="pax.profit"
                    @focus="onPaxFocus(pax)"
                    @blur="updateQuote(pax)"
                    @input="updateFromProfit(index)"
                  />
                </td>
                <td class="text-end">
                  <input
                    type="number"
                    class="cell-input"
                    v-model.number="pax.margin"
                    @focus="onPaxFocus(pax)"
                    @blur="updateQuote(pax)"
                    @input="updateFromMargin(index)"
                  />
                </td>
                <td class="text-end fw-bold">{{ format(pax.gross) }}€</td>
                <td class="text-end text-success" v-if="!caseIsDraft">0,00€</td>
                <td class="text-end">
                  <div class="d-flex align-items-center justify-content-end gap-2">
                    <input
                      type="number"
                      class="cell-input fw-bold text-primary"
                      v-model.number="pax.quote"
                      @focus="onPaxFocus(pax)"
                      @blur="updateQuote(pax)"
                      :disabled="pax.locked"
                    />
                    <span
                      class="cursor-pointer text-dark ms-4"
                      @click="
                        pax.locked = !pax.locked;
                        updateQuote(pax);
                      "
                    >
                      <TheLock v-if="pax.locked" style="width: 14px; height: 14px;" />
                      <TheUnLock v-else style="width: 14px; height: 14px;" />
                    </span>
                  </div>
                </td>
                <template v-if="!caseIsDraft">
                  <td v-for="key in activeExtraKeys" :key="key" class="text-end">
                    <template v-if="pax.invoiceType === key">
                      <input
                        v-if="key === 'free-text'"
                        type="number"
                        class="cell-input"
                        v-model.number="pax.freeValue"
                      />
                      <span v-else class="fw-medium">{{ format(calculateExtra(index, key)) }}€</span>
                    </template>
                    <span v-else class="opacity-25">—</span>
                  </td>
                  <td v-if="getInvoiceRowSpan(pax, index) !== 0" :rowspan="getInvoiceRowSpan(pax, index)">
                    <select
                      v-model="pax.invoiceType"
                      class="simple-select"
                      @change="onInvoiceTypeSelect(pax.invoiceType, pax)"
                    >
                      <option value="">Select</option>
                      <option v-for="t in INVOICE_TYPES" :key="t.key" :value="t.key">
                        {{ t.label }}
                      </option>
                    </select>
                  </td>

                </template>
              </tr>
              <tr v-if="isExpanded(pax.id)" class="pax-detail-row">
                <td :colspan="detailColspan" class="p-0">
                  <div class="pax-detail-wrap px-16 py-12">
                    <div v-if="isSnapshotView(pax)" class="d-flex align-items-center gap-4 mb-8">
                      <span class="badge bg-info-subtle text-info x-small text-uppercase">Quote Snapshot</span>
                      <span class="text-muted x-small">Batch #{{ pax.batch_id }} · {{ quoteSnapshots[pax.batch_id]?.created_at }}</span>
                    </div>
                    <template v-if="getDisplayServices(pax).length">
                      <div v-for="(day, di) in getDisplayServices(pax)" :key="di" class="mb-8">
                        <div class="fw-bold text-uppercase x-small text-muted mb-4">{{ day.route }}</div>
                        <div
                          v-for="(it, ii) in day.items"
                          :key="ii"
                          class="d-flex justify-content-between align-items-center py-2 detail-service-row"
                        >
                          <span class="text-dark">
                            <span class="badge bg-light text-dark me-8 text-uppercase x-small">{{ it.item_type }}</span>
                            {{ it.name }}
                            <span v-if="it.resource_name && it.resource_name !== 'N/A'" class="text-muted">— {{ it.resource_name }}</span>
                            <span v-if="it.qty > 1" class="text-muted"> ×{{ it.qty }}</span>
                          </span>
                          <span class="text-muted fw-medium">{{ format(it.price) }}€</span>
                        </div>
                      </div>
                    </template>
                    <div v-else class="text-muted x-small py-8 text-center">{{ $t("general.shared.noData") }}</div>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>

            <tfoot class="bg-light-soft fw-bold border-top-2">
              <tr>
                <td colspan="2" class="left ps-12 text-uppercase x-small">Totals</td>
                <td class="text-end">{{ format(totalNet) }}€</td>
                <td class="text-end">
                  <input
                    type="number"
                    class="cell-input bg-warning-subtle"
                    v-model.number="bulkProfit"
                    @focus="onTotalFocus"
                    @blur="updateQuote(paxList, true)"
                    @input="applyBulkProfit"
                  />
                </td>
                <td class="text-end">
                  <input
                    type="number"
                    class="cell-input bg-warning-subtle"
                    v-model.number="bulkMargin"
                    @focus="onTotalFocus"
                    @blur="updateQuote(paxList, true)"
                    @input="applyBulkMargin"
                  />
                </td>
                <td class="text-end">{{ format(totalGross) }}€</td>
                <td v-if="!caseIsDraft" class="text-end text-success">0,00€</td>
                <td class="text-end text-primary fw-bolder">{{ format(totalQuote) }}€</td>
                <template v-if="!caseIsDraft">
                  <td v-for="key in activeExtraKeys" :key="key" class="text-end">{{ format(getExtraTotal(key)) }}€</td>
                  <td></td>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-64 border rounded bg-white shadow-sm">
      <v-icon icon="info" size="xxl" class="text-muted mb-16" />
      <h5 class="text-muted">{{ $t("general.shared.noData") }}</h5>
    </div>

    <!-- Off Canvas Components -->
    <the-pricing-invoice-create-off-canvas
      v-if="offCanvasToShow === 'the-pricing-invoice-create-off-canvas'"
      :id="'billing-invoice-off-canvas'"
      :agency="agency.slug"
      :case-id="Number(caseId)"
      :items="{}"
      :pricing="pricing"
      :total-invoice="0"
      :invoice-amount="invoiceRemaingAmount"
      :quote-price="quotePrice"
      :tour-id="0"
      :pre-selected-invoice="selectInvoiceType"
      :pre-selected-pax="displayDropDwonPax"
      offcanvas-class="offcanvas-start"
      version="new"
      @closed="
        closeOffCanvas();
        selectInvoiceType = '';
        displayDropDwonPax = [];
      "
    />

    <the-case-quote-off-canvas
      v-if="offCanvasToShow === 'the-case-quote-off-canvas'"
      :agency="agency.slug"
      :case-id="Number(caseId)"
      :invoice-payload="invoicePayload"
      :client-tour="currentTour"
      :trip-info="tripInfo"
      @get-case-data="getTourData"
      @load-quote="loadQuote"
      @closed="closeOffCanvas"
    />

    <ThePaxInfoEditModal
      v-if="showPaxModal"
      :agency="agency.slug"
      :case-id="Number(caseId)"
      :pax="editPax"
      @reloadPax="loadQuote"
      @closed="showPaxModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { storeToRefs } from "pinia";
import TourDayItemResource from "@/api/objects/TourDayItemResource";
import _ from "lodash";
import { calculatePriceGroup } from "@/composables/pricing";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import ThePricingInvoiceCreateOffCanvas from "@/components/pricing/ThePricingInvoiceCreateOffCanvas.vue";
import TheCasePricingBilling from "@/components/pricing/TheCasePricingBilling.vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import TheCaseQuoteOffCanvas from "@/components/pricing/TheCaseQuoteOffCanvas.vue";
import ThePaxInfoEditModal from "@/components/pax/ThePaxInfoEditModal.vue";
import TheLock from "@/assets/icons/TheLock.vue";
import TheUnLock from "@/assets/icons/TheUnLock.vue";

const props = defineProps<{ caseId: string }>();
const { t } = useI18n();
const INVOICE_TYPES = [
  { key: "30", label: "30%" },
  { key: "30-flights", label: "30% + Flights" },
  { key: "70", label: "70%" },
  { key: "free-text", label: "Free" },
  { key: "flights", label: "Flights" },
];

const pricing = ref<any>({ total: { grossPrice: 0, markup: 0, netPrice: 0 } });
const quotePrice = ref(0);
const paxList = ref<any[]>([]);
const loading = ref(true); // Start as loading
const errorMessage = ref("");
const bulkMargin = ref(0);
const bulkProfit = ref(0);
const selectInvoiceType = ref("");
const selectedPax = ref(0);
const invoiceListData = ref<any[]>([]);
const invoiceRemaingAmount = ref<any>({});
const lock = ref(false);
const initialQuotePrice = ref(0);
const paxEditCache = ref<Record<number, string>>({});
const totalEditCache = ref<string>("");
const displayDropDwonPax = ref<any[]>([]);
const selectedBatchId = ref<number | null>(null);
const tripInfo = ref<any>(null);
const showPaxModal = ref<boolean>(false);
const editPax = ref<any>({});
const caseQuotePrice = ref(0);
const caseQuoteLocked = ref(false);
const expandedPax = ref<Record<number, boolean>>({});
const quoteSnapshots = ref<Record<number, any>>({});
const quoteHistory = ref<{ totalQuotes: number; lastSentDate: string }>({ totalQuotes: 0, lastSentDate: "" });

const authStore = useAuthStore();
const userStore = useUserStore();
const { caseIsDraft } = storeToRefs(userStore);
const agency = computed(() => authStore.agency);
const currentTour = computed(() => userStore.currentTour);
const isDraft = computed(() => userStore.caseIsDraft);
const isSold = computed(() => userStore.caseIsSold);
const toast = useToast();
const { offCanvasToShow, closeOffCanvas, openOffCanvas } = useOffCanvasUtils();

const format = (n: number) => (n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const getLabel = (k: string) => INVOICE_TYPES.find((t) => t.key === k)?.label;

const activeExtraKeys = computed(() => {
  const set = new Set(paxList.value.map((p) => p.invoiceType).filter((v) => v));
  return INVOICE_TYPES.filter((t) => set.has(t.key)).map((t) => t.key);
});

const isAllSelected = computed(() => paxList.value.length > 0 && paxList.value.every((p) => p.selected));
const isAnySelected = computed(() => paxList.value.some((p) => p.selected));

const detailColspan = computed(() => 6 + (caseIsDraft.value ? 0 : 1 + activeExtraKeys.value.length));

const isExpanded = (id: number) => !!expandedPax.value[id];
const toggleExpand = (id: number) => {
  expandedPax.value = { ...expandedPax.value, [id]: !expandedPax.value[id] };
};

const getDisplayServices = (pax: any) => {
  const snap = pax.batch_id ? quoteSnapshots.value[pax.batch_id] : null;
  if (snap?.paxes?.length) {
    const snapPax = snap.paxes.find((p: any) => p.id === pax.id);
    if (snapPax?.services_overview?.length) return snapPax.services_overview;
    const snapSibling = snap.paxes.find((p: any) => p.id !== pax.id && p.services_overview?.length);
    if (snapSibling) return snapSibling.services_overview;
  }

  if (pax.services_overview?.length) return pax.services_overview;

  if (pax.batch_id) {
    const liveSibling = paxList.value.find(
      (p) => p.batch_id === pax.batch_id && p.id !== pax.id && p.services_overview?.length
    );
    if (liveSibling) return liveSibling.services_overview;
  }

  return [];
};

const isSnapshotView = (pax: any) => {
  if (!pax.batch_id) return false;
  const snap = quoteSnapshots.value[pax.batch_id];
  return !!snap?.paxes?.find((p: any) => p.id === pax.id);
};

const getPaxSnapshot = (pax: any) => {
  return JSON.stringify({
    profit: pax.profit,
    margin: pax.margin,
    quote: pax.quote,
    locked: pax.locked,
  });
};

const getTotalSnapshot = () => {
  return JSON.stringify(
    paxList.value.map((p) => ({
      id: p.id,
      profit: p.profit,
      margin: p.margin,
      quote: p.quote,
      locked: p.locked,
    }))
  );
};

const onTotalFocus = () => {
  totalEditCache.value = getTotalSnapshot();
};

const onPaxFocus = (pax: any) => {
  paxEditCache.value[pax.id] = getPaxSnapshot(pax);
};

const toggleAll = (e: any) => {
  const checked = e.target.checked;
  paxList.value.forEach((p) => (p.selected = checked));
};

const calculateExtra = (idx: number, key: string) => {
  const p = paxList.value[idx];
  if (key === "30" || key === "30-flights") return p.gross * 0.3;
  if (key === "70") return p.gross * 0.7;
  if (key === "flights") return p.gross * 0.15;
  if (key === "free-text") return p.freeValue || 0;
  return 0;
};

const updateFromMargin = (i: number) => {
  const p = paxList.value[i];
  p.profit = Number((p.net * (p.margin / 100)).toFixed(2));
  p.gross = Number((p.net + p.profit).toFixed(2));
  if (!p.locked) p.quote = p.gross;
};

const updateFromProfit = (i: number) => {
  const p = paxList.value[i];
  p.margin = p.net > 0 ? Number(((p.profit / p.net) * 100).toFixed(2)) : 0;
  p.gross = Number((p.net + p.profit).toFixed(2));
  if (!p.locked) p.quote = p.gross;
};

const applyBulkMargin = () =>
  paxList.value.forEach((_, i) => {
    paxList.value[i].margin = bulkMargin.value;
    updateFromMargin(i);
  });

const applyBulkProfit = () => {
  const tNet = totalNet.value;
  paxList.value.forEach((p, i) => {
    paxList.value[i].profit = tNet > 0 ? Number(((p.net / tNet) * bulkProfit.value).toFixed(2)) : 0;
    updateFromProfit(i);
  });
};

const sum = (key: string) => Number(paxList.value.reduce((s, p) => s + Number(p[key] || 0), 0).toFixed(2));

const totalNet = computed(() => sum("net"));
const totalGross = computed(() => sum("gross"));
const totalQuote = computed(() => sum("quote"));
const totalPaid = 0.0;
const getExtraTotal = (k: string) => paxList.value.reduce((s, _, i) => s + calculateExtra(i, k), 0);

const invoicePayload = computed(() => paxList.value.filter((pax) => pax.selected));

const openCreateBillingInvoiceOffCanvas = () => {
  openOffCanvas("the-pricing-invoice-create-off-canvas");
};

const getBatchRowClass = (pax: any) => {
  if (!pax.batch_id) return "";
  return `batch-border-${String(pax.batch_id).slice(-1)}`;
};

const groupedPaxList = computed(() => {
  const withBatch = paxList.value.filter((p) => p.batch_id).sort((a, b) => a.batch_id - b.batch_id);
  const withoutBatch = paxList.value.filter((p) => !p.batch_id);
  return [...withBatch, ...withoutBatch];
});

const getRowSpan = (pax: any, index: number) => {
  if (!pax.batch_id) return 1 + (isExpanded(pax.id) ? 1 : 0);
  if (!isFirstRowOfBatch(pax, index)) return 0;
  const batchPaxes = groupedPaxList.value.filter((p) => p.batch_id === pax.batch_id);
  const expandedCount = batchPaxes.filter((p) => isExpanded(p.id)).length;
  return batchPaxes.length + expandedCount;
};

const getInvoiceRowSpan = (pax: any, index: number) => {
  if (!pax.batch_id) return 1 + (isExpanded(pax.id) ? 1 : 0);
  if (!isFirstRowOfBatch(pax, index)) return 0;
  const batchPaxes = groupedPaxList.value.filter((p) => p.batch_id === pax.batch_id);
  const expandedCount = batchPaxes.filter((p) => isExpanded(p.id)).length;
  return batchPaxes.length + expandedCount;
};

const isFirstRowOfBatch = (pax: any, index: number) => {
  if (!pax.batch_id) return true;
  if (index === 0) return true;
  return groupedPaxList.value[index - 1].batch_id !== pax.batch_id;
};

const onInvoiceTypeSelect = (type: string, pax: any) => {
  if (!type) return;
  let selectedIds: number[] = [];

  if (pax.batch_id) {
    const batchPax = paxList.value.filter((p) => p.batch_id === pax.batch_id);
    batchPax.forEach((p) => { p.invoiceType = type; });
    selectedIds = batchPax.map((p) => p.id);
  } else {
    pax.invoiceType = type;
    selectedIds = [pax.id];
  }
  displayDropDwonPax.value = selectedIds;
  selectInvoiceType.value = type;
  selectedPax.value = selectedIds[0];

  openOffCanvas("the-pricing-invoice-create-off-canvas");
};

const toggleBatch = (pax: any, checked: boolean) => {
  if (!pax.batch_id) {
    pax.selected = checked;
    return;
  }
  if (checked) {
    selectedBatchId.value = pax.batch_id;
    paxList.value.forEach((p) => { p.selected = p.batch_id === pax.batch_id; });
  } else {
    selectedBatchId.value = null;
    paxList.value.forEach((p) => { if (p.batch_id === pax.batch_id) p.selected = false; });
  }
};

const isCheckboxDisabled = (pax: any) => {
  if (!selectedBatchId.value) return false;
  return pax.batch_id !== selectedBatchId.value;
};

const toggleLock = () => { lock.value = !lock.value; };

const openEditPaxModal = (pax: any) => {
  editPax.value = pax;
  showPaxModal.value = true;
};

const updateQuote = async (pax: any, isTotal = false) => {
  let data = null;
  if (isTotal) {
    const oldSnap = totalEditCache.value;
    const newSnap = getTotalSnapshot();
    if (oldSnap === newSnap) return;
    data = paxList.value;
  } else {
    const oldSnap = paxEditCache.value[pax.id];
    const newSnap = getPaxSnapshot(pax);
    if (oldSnap === newSnap) return;
    data = [pax];
  }

  const updateQuoteData = data.map((p) => ({
    pax_id: p.id,
    batch_id: p.batch_id ?? null,
    profit: p.profit,
    margin: p.margin,
    waw_price: p.quote,
    waw_price_locked: p.locked,
  }));

  try {
    const response = await api.case.updateQuote(authStore.agency.slug, Number(props.caseId), { quotes: updateQuoteData });
    if (response) {
      toast.success(t("general.shared.savedChanges"));
      totalEditCache.value = getTotalSnapshot();
      if (!isTotal) paxEditCache.value[pax.id] = getPaxSnapshot(pax);
      getTourData();
      await loadQuote();
    }
  } catch (e) {
    console.error(e);
  }
};

const sendQuoteOffcanvas = () => {
  openOffCanvas("the-case-quote-off-canvas");
};

const startNewQuote = () => {
  paxList.value.forEach((p) => {
    p.batch_id = null;
    p.locked = false;
    p.selected = false;
  });
  selectedBatchId.value = null;
  toast.info("New quote started — adjust prices and send to create a new quote batch.");
};

const loadQuote = async () => {
  errorMessage.value = "";
  try {
    const res: any = await api.case.caseQuote(authStore.agency.slug, Number(props.caseId));
    tripInfo.value = res.content.trip_info;
    quoteSnapshots.value = res.content.quote_snapshots || {};
    paxList.value = res.content.paxes.map((p: any) => {
      const net = Number(p.total_price);
      const profit = p.profit || 0;
      const margin = p.margin || 0;
      const gross = net + profit;
      const quote = Number(p.waw_price);

      return {
        ...p,
        id: p.id,
        batch_id: p.batch_id,
        name: p.name,
        type: p.type || "Adult",
        net,
        profit,
        margin,
        gross,
        quote,
        locked: p.waw_price_locked,
        selected: false,
        invoiceType: "",
        freeValue: 0,
      };
    });

    caseQuotePrice.value = Number(currentTour.value?.quote_price) || 0;
    caseQuoteLocked.value = !!currentTour.value?.is_quote_price_locked;

    try {
      const histRes: any = await api.case.quoteHistory(authStore.agency.slug, Number(props.caseId));
      const histContent = histRes?.content;
      if (histContent) {
        const quotes = histContent.quotes || [];
        quoteHistory.value = {
          totalQuotes: histContent.total_quotes_sent || quotes.length,
          lastSentDate: quotes.length > 0 ? (quotes[0].sent_at || quotes[0].created_at) : "",
        };
      }
    } catch {
      // Quote history is optional — don't fail the whole load
    }
  } catch (error: any) {
    errorMessage.value = error?.response?.data?.message || t("general.shared.errorOccurred");
  } finally {
    bulkProfit.value = paxList.value.reduce((s, p) => s + Number(p.profit), 0);
    bulkMargin.value = paxList.value.reduce((s, p) => s + Number(p.margin), 0);
  }
};

const getTourData = async () => {
  await userStore.setCurrentCase(agency.value.slug, Number(props.caseId));
};

const loadTourPricing = async () => {
  try {
    const data = await api.case.casePricingDetails(agency.value.slug, Number(props.caseId));
    pricing.value = {
      total: {
        netPrice: Number(currentTour.value.net_price),
        grossPrice: Number(currentTour.value.gross_price),
        markup: Number(currentTour.value.markup),
      }
    };
  } catch (e) {
    console.error(e);
  }
};

const retryLoad = async () => {
  loading.value = true;
  await loadTourPricing();
  await loadQuote();
  loading.value = false;
};

onMounted(async () => {
  loading.value = true;
  await getTourData();
  await loadTourPricing();
  await loadQuote();
  loading.value = false;
});
</script>

<style lang="scss" scoped>
.quote-summary-card {
  border-left: 4px solid #fd0c6f;
}

.quote-table {
  font-size: 0.75rem;

  thead th {
    padding: 8px 10px;
    font-size: 0.65rem;
    white-space: nowrap;
    &.left { text-align: left; }
  }

  tbody td {
    padding: 4px 10px;
    height: 32px;
    vertical-align: middle;
    border-right: 1px solid #f0f0f0;
    &.left { text-align: left; }
  }
}

.row-selected {
  background-color: rgba(253, 12, 111, 0.08) !important;
}

.cell-input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1px 4px;
  text-align: right;
  height: 24px;
  font-size: 0.75rem;
  &:focus {
    outline: none;
    border-color: #fd0c6f;
    box-shadow: 0 0 0 2px rgba(253, 12, 111, 0.12);
  }
}

.simple-select {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0 2px;
  font-size: 0.7rem;
  height: 22px;
  background: #fff;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #fd0c6f;
    box-shadow: 0 0 0 2px rgba(253, 12, 111, 0.12);
  }
}

.btn-edit-inline {
  opacity: 0.2;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
}

.btn-expand-inline {
  background: transparent;
  border: none;
  color: #666;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: #fd0c6f; }
}

.pax-detail-row > td {
  background-color: #fafafa;
  border-bottom: 1px solid #eee;
}

.pax-detail-wrap {
  font-size: 0.72rem;

  .detail-service-row {
    border-bottom: 1px dashed #ececec;
    &:last-child { border-bottom: none; }
  }
}

:deep(.btn-icon:not(.rounded-circle)) {
  border-radius: 6px !important;
}

:deep(.btn:not(.rounded-circle)) {
  border-radius: 6px !important;
}

:deep(.btn-primary) {
  background-color: #fd0c6f !important;
  border-color: #fd0c6f !important;
  color: #fff !important;
}

:deep(.btn-primary:hover) {
  background-color: #dc095f !important;
  border-color: #dc095f !important;
}

.quote-action-btn {
  letter-spacing: 0.3px !important;
  min-height: 38px;
}

tr:hover .btn-edit-inline {
  opacity: 0.6;
}

.checkbox-cell {
  text-align: center;
  background-color: inherit;
}

.chk { transform: scale(1.1); }

.border-top-2 { border-top: 2px solid #ddd !important; }

/* Batch styling */
[class*="batch-border-"] {
  border-left: 4px solid transparent;
}
.batch-border-0 { border-left-color: #ec4899; }
.batch-border-1 { border-left-color: #3b82f6; }
.batch-border-2 { border-left-color: #22c55e; }
.batch-border-3 { border-left-color: #f97316; }
.batch-border-4 { border-left-color: #8b5cf6; }
.batch-border-5 { border-left-color: #14b8a6; }
</style>
